import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const useVisualizationJob = (token, onComplete) => {
  const [job, setJob] = useState(null);
  const timerRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const stopPolling = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => {
    if (!job || job.status !== 'running') return stopPolling;
    timerRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/admin/fabrics/visualization-jobs/${job.id}`, { headers });
        setJob(data);
        if (data.status === 'completed') {
          stopPolling();
          const ok = data.results.filter((r) => r.status === 'success').length;
          const failed = data.results.length - ok;
          toast[failed ? 'warning' : 'success'](
            `Regeneration finished: ${ok} succeeded${failed ? `, ${failed} failed` : ''}`
          );
          onComplete?.(data);
        }
      } catch {
        stopPolling();
        toast.error('Lost connection to regeneration job');
      }
    }, 3000);
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status]);

  const startJob = useCallback(async (colorIds, roomType = 'living_room') => {
    try {
      const ids = Array.isArray(colorIds) ? colorIds : [colorIds];
      const url = ids.length === 1
        ? `${API_URL}/admin/fabrics/colors/${ids[0]}/regenerate`
        : `${API_URL}/admin/fabrics/colors/regenerate-bulk`;
      const { data } = await axios.post(url, ids.length === 1 ? { room_type: roomType } : { color_ids: ids, room_type: roomType }, { headers });
      setJob(data);
      toast.info(`Regenerating ${ids.length} visualization${ids.length > 1 ? 's' : ''} in the background`);
      return data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start regeneration');
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return { job, startJob, adoptJob: setJob, isRunning: job?.status === 'running', dismissJob: () => setJob(null) };
};
